import React, { useCallback, useEffect, useState } from "react";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableItem,
  DataTableRow,
  Icon,
} from "../../../components/Component";
import { Input, Label } from "reactstrap";
import http from "../../../utils/http-common";
import { isAbortError } from "../../../utils/asyncRequestGuards";
import TeleconsultationPageShell from "../../../components/admin/TeleconsultationPageShell";

const PLACEMENT = "header_services";

export default function NavigationItemsAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [label, setLabel] = useState("");
  const [href, setHref] = useState("");
  const [column, setColumn] = useState("0");
  const [order, setOrder] = useState("0");
  const [saving, setSaving] = useState(false);

  const load = useCallback((signal) => {
    setLoading(true);
    setErr("");
    http
      .get("/api/v1/admin/navigation-items", { params: { placement: PLACEMENT }, signal })
      .then((res) => {
        const list = res.data?.data;
        setRows(Array.isArray(list) ? list : []);
      })
      .catch((e) => {
        if (isAbortError(e)) return;
        setErr(e.response?.data?.error || "Failed to load navigation items");
        setRows([]);
      })
      .finally(() => {
        if (!signal?.aborted) setLoading(false);
      });
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const addRow = () => {
    const col = Number(column);
    const ord = Number(order);
    if (!label.trim() || !href.trim()) {
      setErr("Label and href are required.");
      return;
    }
    if (!Number.isFinite(col) || col < 0 || col > 2) {
      setErr("Column must be 0, 1, or 2.");
      return;
    }
    setSaving(true);
    setErr("");
    http
      .post("/api/v1/admin/navigation-items", {
        placement: PLACEMENT,
        label: label.trim(),
        href: href.trim(),
        column: col,
        order: Number.isFinite(ord) ? ord : 0,
        enabled: true,
      })
      .then(() => {
        setLabel("");
        setHref("");
        setColumn("0");
        setOrder("0");
        load();
      })
      .catch((e) => {
        setErr(e.response?.data?.error || "Create failed");
      })
      .finally(() => setSaving(false));
  };

  const del = (id) => {
    if (!window.confirm("Delete this link?")) return;
    http
      .delete(`/api/v1/admin/navigation-items/${id}`)
      .then(() => load())
      .catch((e) => setErr(e.response?.data?.error || "Delete failed"));
  };

  return (
    <TeleconsultationPageShell
      headTitle="Header navigation (Services mega menu)"
      title="Header navigation — Services mega menu"
      description={`Placement « ${PLACEMENT} ». Items appear in 3 columns on the public site. Empty list falls back to built-in defaults until you add rows.`}
    >
      <Block>
        <BlockHead>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">Add link</BlockTitle>
              <BlockDes className="text-soft">Column 0 = left, 1 = middle, 2 = right.</BlockDes>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <div className="admin-list-filters">
          <div className="admin-list-filters__field">
            <Label>Label</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Teleconsultation" />
          </div>
          <div className="admin-list-filters__field" style={{ flex: "1 1 240px", maxWidth: 360 }}>
            <Label>URL path</Label>
            <Input value={href} onChange={(e) => setHref(e.target.value)} placeholder="/teleconsultation" />
          </div>
          <div className="admin-list-filters__field" style={{ flex: "0 1 100px", minWidth: 90 }}>
            <Label>Column (0–2)</Label>
            <Input value={column} onChange={(e) => setColumn(e.target.value)} type="number" min={0} max={2} />
          </div>
          <div className="admin-list-filters__field" style={{ flex: "0 1 100px", minWidth: 90 }}>
            <Label>Order</Label>
            <Input value={order} onChange={(e) => setOrder(e.target.value)} type="number" min={0} />
          </div>
          <div className="admin-list-filters__actions">
            <Button color="primary" disabled={saving} onClick={addRow}>
              Add
            </Button>
          </div>
        </div>
        {err ? (
          <p className="text-danger mt-3 mb-0" role="alert">
            {err}
          </p>
        ) : null}
      </Block>

      <Block>
        <BlockHead>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h4">Current items ({rows.length})</BlockTitle>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button outline color="light" size="sm" onClick={() => load()}>
                <Icon name="reload" /> Refresh
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <DataTable className="card-stretch">
          <DataTableHead>
            <DataTableRow>
              <DataTableItem>Column</DataTableItem>
              <DataTableItem>Order</DataTableItem>
              <DataTableItem>Label</DataTableItem>
              <DataTableItem>Href</DataTableItem>
              <DataTableItem className="text-end">Actions</DataTableItem>
            </DataTableRow>
          </DataTableHead>
          <DataTableBody>
            {loading ? (
              <DataTableRow>
                <DataTableItem colSpan={5}>Loading…</DataTableItem>
              </DataTableRow>
            ) : rows.length === 0 ? (
              <DataTableRow>
                <DataTableItem colSpan={5}>
                  No rows — the site uses default links until you add some here.
                </DataTableItem>
              </DataTableRow>
            ) : (
              rows.map((r) => (
                <DataTableRow key={r._id}>
                  <DataTableItem>{r.column}</DataTableItem>
                  <DataTableItem>{r.order}</DataTableItem>
                  <DataTableItem>{r.label}</DataTableItem>
                  <DataTableItem>
                    <code className="small">{r.href}</code>
                  </DataTableItem>
                  <DataTableItem className="text-end">
                    <Button color="danger" size="sm" outline onClick={() => del(r._id)}>
                      Delete
                    </Button>
                  </DataTableItem>
                </DataTableRow>
              ))
            )}
          </DataTableBody>
        </DataTable>
      </Block>
    </TeleconsultationPageShell>
  );
}
